/**
 * API Configuration
 * Centralized API URL configuration for all API calls
 */

import axios, { AxiosError } from 'axios';
import { logger } from '../utils/logger';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Error codes for categorizing errors
export const ApiErrorCode = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  CORS_ERROR: 'CORS_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ApiErrorCode = typeof ApiErrorCode[keyof typeof ApiErrorCode];

export interface ApiError {
  code: ApiErrorCode;
  message: string;
  details?: string;
  status?: number;
}

// Configure axios interceptors for global error handling
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const apiError = categorizeError(error);

    // Log all API errors
    logger.error('API Error', {
      code: apiError.code,
      message: apiError.message,
      details: apiError.details,
      status: apiError.status,
      url: error.config?.url,
      method: error.config?.method,
    }, error);

    return Promise.reject(apiError);
  }
);

function categorizeError(error: AxiosError): ApiError {
  // Network errors (no response received)
  if (!error.response) {
    if (error.code === 'ERR_NETWORK') {
      return {
        code: ApiErrorCode.NETWORK_ERROR,
        message: 'Unable to connect to the server. Please check your internet connection.',
        details: `Failed to reach ${error.config?.url}`,
      };
    }

    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return {
        code: ApiErrorCode.TIMEOUT_ERROR,
        message: 'Request timed out. The server is taking too long to respond.',
        details: error.message,
      };
    }

    // Likely CORS error
    return {
      code: ApiErrorCode.CORS_ERROR,
      message: 'Cannot connect to the server. This may be a CORS configuration issue.',
      details: `Check CORS settings for ${error.config?.url}`,
    };
  }

  // HTTP status-based errors
  const status = error.response.status;
  const responseData = error.response.data as Record<string, unknown>;

  // Helper to safely extract string from unknown
  const getString = (value: unknown): string | undefined => {
    return typeof value === 'string' ? value : undefined;
  };

  switch (status) {
    case 401:
      return {
        code: ApiErrorCode.AUTH_ERROR,
        message: 'Authentication required. Please log in.',
        details: getString(responseData?.message) || getString(responseData?.error),
        status,
      };

    case 403:
      return {
        code: ApiErrorCode.AUTH_ERROR,
        message: 'Access denied. You don\'t have permission to perform this action.',
        details: getString(responseData?.message) || getString(responseData?.error),
        status,
      };

    case 404:
      return {
        code: ApiErrorCode.NOT_FOUND,
        message: 'Resource not found.',
        details: getString(responseData?.message) || getString(responseData?.error),
        status,
      };

    case 400:
    case 422:
      return {
        code: ApiErrorCode.VALIDATION_ERROR,
        message: getString(responseData?.message) || 'Invalid request. Please check your input.',
        details: getString(responseData?.error) || getString(responseData?.details),
        status,
      };

    case 500:
    case 502:
    case 503:
    case 504:
      return {
        code: ApiErrorCode.SERVER_ERROR,
        message: 'Server error. Please try again later.',
        details: getString(responseData?.message) || getString(responseData?.error),
        status,
      };

    default:
      return {
        code: ApiErrorCode.UNKNOWN_ERROR,
        message: 'An unexpected error occurred.',
        details: getString(responseData?.message) || getString(responseData?.error) || error.message,
        status,
      };
  }
}

export const API_ENDPOINTS = {
  // Auth endpoints
  auth: {
    me: `${API_BASE_URL}/api/auth/me`,
    login: `${API_BASE_URL}/api/auth/login`,
    register: `${API_BASE_URL}/api/auth/register`,
    logout: `${API_BASE_URL}/api/auth/logout`,
  },

  // RFP endpoints
  rfp: {
    upload: `${API_BASE_URL}/api/rfp/upload`,
    update: (rfpId: string) => `${API_BASE_URL}/api/rfp/${rfpId}`,
    generate: (rfpId: string) => `${API_BASE_URL}/api/rfp/generate/${rfpId}`,
    cancel: (rfpId: string) => `${API_BASE_URL}/api/rfp/${rfpId}/cancel`,
    question: (questionId: string) => `${API_BASE_URL}/api/rfp/questions/${questionId}`,
    regenerate: (questionId: string) => `${API_BASE_URL}/api/rfp/questions/${questionId}/regenerate`,
    skip: (questionId: string) => `${API_BASE_URL}/api/rfp/questions/${questionId}/skip`,
  },

  // Context endpoints
  context: {
    list: `${API_BASE_URL}/api/context`,
    upload: `${API_BASE_URL}/api/context/upload`,
    delete: (id: string) => `${API_BASE_URL}/api/context/${id}`,
    deleteFolder: (path: string) => `${API_BASE_URL}/api/context/folder/${encodeURIComponent(path)}`,
    reindex: `${API_BASE_URL}/api/context/reindex`,
  },

  // Google Drive endpoints
  drive: {
    status: `${API_BASE_URL}/api/drive/status`,
    auth: `${API_BASE_URL}/api/drive/auth`,
    selectFolder: `${API_BASE_URL}/api/drive/select-folder`,
    sync: `${API_BASE_URL}/api/drive/sync`,
    disconnect: `${API_BASE_URL}/api/drive/disconnect`,
    folders: `${API_BASE_URL}/api/drive/folders`,
    folderContents: (folderId: string, orderBy?: string) =>
      `${API_BASE_URL}/api/drive/folders/${folderId}/contents${orderBy ? `?orderBy=${orderBy}` : ''}`,
  },

  // Proposal endpoints
  proposals: {
    get: (proposalId: string) => `${API_BASE_URL}/api/rfp/history/${proposalId}`,
    history: `${API_BASE_URL}/api/rfp/history`,
    delete: (proposalId: string) => `${API_BASE_URL}/api/rfp/history/${proposalId}`,
  },

  // Proposal review endpoints (old single-file review)
  proposal: {
    upload: `${API_BASE_URL}/api/proposal/upload`,
    review: (proposalId: string) => `${API_BASE_URL}/api/proposal/review/${proposalId}`,
  },

  // Review endpoints (new dual-file review)
  review: {
    submit: `${API_BASE_URL}/api/review`,
    test: `${API_BASE_URL}/api/review/test`, // Mock endpoint for testing
    history: `${API_BASE_URL}/api/review/history`,
    get: (reviewId: string) => `${API_BASE_URL}/api/review/${reviewId}`,
    delete: (reviewId: string) => `${API_BASE_URL}/api/review/${reviewId}`,
  },

  // User endpoints
  users: {
    stats: `${API_BASE_URL}/api/users/stats`,
  },
} as const;
